<script setup>
    import {
        ActivityIcon,
        AddIcon,
        BorrowingIcon,
        BundleIcon,
        CategoryIcon,
        ClientIcon,
        DashboardIcon,
        MerchandiseIcon,
        OrderIcon,
        ProductIcon,
        RoleIcon,
        ServiceIcon,
        SettingIcon,
        UserIcon,
    } from '@/components/icons'
    import { Dropdown, DropdownLink } from '@/components/shared'
    import { useActiveRoute } from '@/composables/useActiveRoute'
    import { usePermissions } from '@/composables/usePermissions'
    import SidebarCashbox from '@/layouts/partials/SidebarCashbox.vue'
    import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'
    import { computed, ref } from 'vue'

    const { canAccess } = usePermissions()
    const { isActive } = useActiveRoute()

    const sidebarOpen = ref(false)

    const navigations = computed(() => [
        {
            title: 'Přehled',
            data: [
                {
                    name: 'Přehled',
                    href: route('home.index'),
                    icon: DashboardIcon,
                    current: isActive('home.index'),
                    condition: null, // Dashboard is always visible
                },
            ],
        },
        {
            title: 'Výpůjčky',
            data: [
                {
                    name: 'Výpůjčky',
                    href: route('borrowings.index'),
                    icon: BorrowingIcon,
                    current: isActive('borrowings.*'),
                    condition: 'view_borrowing',
                },
                {
                    name: 'Produkty',
                    href: route('products.index'),
                    icon: ProductIcon,
                    current: isActive('products.*'),
                    condition: 'view_product',
                },
                {
                    name: 'Balíčky',
                    href: route('bundles.index'),
                    icon: BundleIcon,
                    current: isActive('bundles.*'),
                    condition: 'view_bundle',
                },
                {
                    name: 'Kategorie',
                    href: route('categories.index'),
                    icon: CategoryIcon,
                    current: isActive('categories.*'),
                    condition: 'view_category',
                },
            ],
        },
        {
            title: 'Prodej',
            data: [
                {
                    name: 'Objednávky',
                    href: route('orders.index'),
                    icon: OrderIcon,
                    current: isActive('orders.*'),
                    condition: 'view_order',
                },
                {
                    name: 'Služby',
                    href: route('services.index'),
                    icon: ServiceIcon,
                    current: isActive('services.*'),
                    condition: 'view_service',
                },
                {
                    name: 'Zboží',
                    href: route('merchandises.index'),
                    icon: MerchandiseIcon,
                    current: isActive('merchandises.*'),
                    condition: 'view_merchandise',
                },
            ],
        },
        {
            title: 'Správa',
            data: [
                {
                    name: 'Klienti',
                    href: route('clients.index'),
                    icon: ClientIcon,
                    current: isActive('clients.*'),
                    condition: 'view_client',
                },
                {
                    name: 'Zaměstnanci',
                    href: route('users.index'),
                    icon: UserIcon,
                    current: isActive('users.*'),
                    condition: 'view_user',
                },
                {
                    name: 'Role',
                    href: route('roles.index'),
                    icon: RoleIcon,
                    current: isActive('roles.*'),
                    condition: 'view_role',
                },
                {
                    name: 'Aktivity',
                    href: route('activities.index'),
                    icon: ActivityIcon,
                    current: isActive('activities.*'),
                    condition: 'view_activity',
                },
                {
                    name: 'Nastavení',
                    href: route('settings.index'),
                    icon: SettingIcon,
                    current: isActive('settings.*'),
                    condition: 'view_setting',
                },
            ],
        },
    ])
</script>

<template>
    <TransitionRoot
        as="template"
        :show="sidebarOpen"
    >
        <Dialog
            class="relative z-50 xl:hidden"
            @close="sidebarOpen = false"
        >
            <TransitionChild
                as="template"
                enter="transition-opacity ease-linear duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div class="fixed inset-0 flex">
                <TransitionChild
                    as="template"
                    enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full"
                    enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leave-from="translate-x-0"
                    leave-to="-translate-x-full"
                >
                    <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div class="absolute top-0 left-full flex w-16 justify-center pt-5">
                                <button
                                    type="button"
                                    class="-m-2.5 p-2.5"
                                    @click="sidebarOpen = false"
                                >
                                    <span class="sr-only">Close sidebar</span>
                                    <XMarkIcon
                                        class="size-6 text-white"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </TransitionChild>

                        <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                            <div class="flex h-16 shrink-0 items-center justify-between">
                                <Link :href="route('home.index')">
                                    <img
                                        class="h-8 w-auto rounded-lg object-contain"
                                        src="/images/logo.png"
                                        alt="BrainCode s.r.o."
                                    />
                                </Link>
                            </div>

                            <nav class="flex flex-1 flex-col">
                                <ul
                                    role="list"
                                    class="flex flex-1 flex-col gap-y-7"
                                >
                                    <!-- New Borrowing Button -->
                                    <li>
                                        <Link
                                            :href="route('borrowings.create')"
                                            class="bg-brand-primary flex items-center justify-center rounded-lg p-2 font-semibold tracking-wide text-white"
                                            @click="sidebarOpen = false"
                                            v-if="canAccess('create_borrowing')"
                                        >
                                            <AddIcon class="mr-2 size-6" />
                                            <span>Nová výpůjčka</span>
                                        </Link>
                                    </li>

                                    <!-- Navigation Groups -->
                                    <li
                                        v-for="data in navigations"
                                        :key="data.title"
                                    >
                                        <div
                                            class="text-xs/6 font-semibold text-gray-400"
                                            v-if="
                                                data.title &&
                                                data.data.some((item) => !item.condition || canAccess(item.condition))
                                            "
                                        >
                                            {{ data.title }}
                                        </div>

                                        <ul
                                            role="list"
                                            class="-mx-2 mt-2 space-y-1"
                                        >
                                            <li
                                                v-for="item in data.data"
                                                :key="item.name"
                                                v-show="!item.condition || canAccess(item.condition)"
                                            >
                                                <Link
                                                    :href="item.href"
                                                    :class="[
                                                        item.current
                                                            ? 'bg-gray-100 text-green-600'
                                                            : 'text-gray-700 hover:bg-gray-50 hover:text-green-600',
                                                        'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                                                    ]"
                                                    @click="sidebarOpen = false"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            item.current
                                                                ? 'text-green-600'
                                                                : 'text-gray-400 group-hover:text-green-600',
                                                            'size-6 shrink-0',
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    {{ item.name }}
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>

                                <SidebarCashbox
                                    class="mt-5"
                                    v-show="canAccess('view_cash-transaction')"
                                />

                                <!-- User Profile -->
                                <div class="-mx-6 mt-auto border-t border-gray-200">
                                    <Dropdown
                                        class="relative"
                                        width="48"
                                        align="left"
                                        direction="up"
                                    >
                                        <template #trigger>
                                            <button
                                                type="button"
                                                class="flex w-full items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50"
                                            >
                                                <UserIcon class="size-8 rounded-full" />
                                                <span aria-hidden="true">
                                                    {{ $page.props.auth.user.email }}
                                                </span>
                                            </button>
                                        </template>
                                        <template #content>
                                            <DropdownLink :href="route('profile.show')"> Profil </DropdownLink>

                                            <DropdownLink
                                                :href="route('logout')"
                                                method="post"
                                            >
                                                Odhlásit se
                                            </DropdownLink>
                                        </template>
                                    </Dropdown>
                                </div>
                            </nav>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- Desktop sidebar -->
    <div class="hidden xl:fixed xl:inset-y-0 xl:z-99 xl:flex xl:w-72 xl:flex-col">
        <div class="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
            <div class="flex h-16 shrink-0 items-center space-x-4">
                <Link :href="route('home.index')">
                    <img
                        class="h-8 w-auto rounded-lg object-contain"
                        src="/images/logo.png"
                        alt="BrainCode s.r.o."
                    />
                </Link>

                <li
                    class="grid w-full"
                    v-if="canAccess('create_borrowing')"
                >
                    <Link
                        :href="route('borrowings.create')"
                        class="bg-brand-primary flex items-center justify-center rounded-lg p-2 font-semibold tracking-wide text-white"
                    >
                        <AddIcon class="mr-2 size-6" />
                        <span>Nová výpůjčka</span>
                    </Link>
                </li>
            </div>

            <nav class="flex flex-1 flex-col">
                <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7"
                >
                    <li
                        v-for="data in navigations"
                        :key="data.title"
                    >
                        <div
                            class="text-xs/6 font-semibold text-gray-400"
                            v-if="data.title && data.data.some((item) => !item.condition || canAccess(item.condition))"
                        >
                            {{ data.title }}
                        </div>

                        <ul
                            role="list"
                            class="-mx-2 space-y-0.5"
                        >
                            <li
                                v-for="item in data.data"
                                :key="item.name"
                                v-show="!item.condition || canAccess(item.condition)"
                            >
                                <Link
                                    :href="item.href"
                                    :class="[
                                        item.current
                                            ? 'border-gray-200 bg-gray-100 text-green-600'
                                            : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-green-600',
                                        'group flex items-center gap-x-3 rounded-lg border py-2 pl-2 text-sm/6 font-semibold',
                                    ]"
                                >
                                    <component
                                        :is="item.icon"
                                        :class="[
                                            item.current
                                                ? 'border-zinc-200 bg-white text-green-600 shadow'
                                                : 'border-transparent text-gray-400 group-hover:text-green-600',
                                            'size-7 shrink-0 rounded-md border p-0.5',
                                        ]"
                                        aria-hidden="true"
                                    />
                                    {{ item.name }}
                                </Link>
                            </li>
                        </ul>
                    </li>
                </ul>

                <SidebarCashbox
                    class="mt-5"
                    v-show="canAccess('view_cash-transaction')"
                />

                <div>
                    <li class="-mx-6 mt-auto border-t border-gray-200">
                        <Dropdown
                            class="relative"
                            width="48"
                            align="left"
                            direction="up"
                        >
                            <template #trigger>
                                <button
                                    type="button"
                                    class="flex w-full items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50"
                                >
                                    <UserIcon class="size-8 rounded-full" />
                                    <span aria-hidden="true">
                                        {{ $page.props.auth.user.email }}
                                    </span>
                                </button>
                            </template>
                            <template #content>
                                <DropdownLink :href="route('profile.show')"> Profil </DropdownLink>

                                <DropdownLink
                                    :href="route('logout')"
                                    method="post"
                                >
                                    Odhlásit se
                                </DropdownLink>
                            </template>
                        </Dropdown>
                    </li>
                </div>
            </nav>
        </div>
    </div>

    <!-- Mobile top bar with hamburger menu -->
    <div
        class="sticky top-0 z-40 flex items-center gap-x-4 border-b border-gray-200 bg-white px-4 py-4 shadow-sm sm:gap-x-6 sm:px-6 xl:hidden"
    >
        <button
            type="button"
            class="-m-2.5 p-2.5 text-gray-700"
            @click="sidebarOpen = true"
        >
            <span class="sr-only">Otevřít menu</span>
            <Bars3Icon
                class="size-6"
                aria-hidden="true"
            />
        </button>

        <div class="flex flex-1 items-center justify-between gap-x-4">
            <Link :href="route('home.index')">
                <img
                    class="h-8 w-auto rounded-lg object-contain"
                    src="/images/logo.png"
                    alt="BrainCode s.r.o."
                />
            </Link>

            <Link
                v-if="canAccess('create_borrowing')"
                :href="route('borrowings.create')"
                class="bg-brand-primary flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold tracking-wide text-white"
            >
                <AddIcon class="size-5 sm:mr-2" />
                <span class="hidden sm:inline">Nová výpůjčka</span>
            </Link>
        </div>
    </div>
</template>
